<template>
  <div ref="header" class="menu-item" :class="{ active: active }">
    <component
      :is="to ? RouterLink : 'div'"
      v-bind="to ? { to } : {}"
      class="trigger-btn cursor-pointer"
      @click="() => toggle()"
    >
      <template v-if="!minimized">
        <div class="flex items-center gap-3 px-4 py-[10px]">
          <div v-if="active" class="absolute left-0 h-6 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]" />
          <UiIcon :icon="icon" :size="32" class="shrink-0" />
          <span class="grow">{{ name }}</span>
        </div>
      </template>
      <div v-else class="flex h-12 w-12 items-center justify-center">
        <div v-if="active" class="absolute left-0 h-6 w-0.5 rounded-sm bg-[--c-primary-900] dark:bg-[--c-primary-200]" />
        <UiIcon :icon="icon" :size="32" />
      </div>
    </component>
    <div
      v-if="!minimized && isOpen"
      class="ml-7 flex flex-col gap-2 border-0 border-l border-neutral-200 dark:border-neutral-50/10"
    >
      <slot name="items" />
    </div>
    <Teleport v-if="minimized" defer to="body">
      <OnClickOutside @trigger="() => close()">
        <Transition
          mode="out-in"
          enter-active-class="transition ease-in-out duration-[.5s] transform"
          enter-from-class="opacity-0"
          leave-active-class="transition ease-in-out duration-[.5s] transform"
          leave-to-class="opacity-0"
        >
          <div
            v-if="open"
            class="fixed z-10 inset-0 left-20 w-64 border-0 border-r border-solid border-neutral-200 bg-neutral-50/40 dark:bg-[rgba(46,46,46,0.4)] backdrop-blur-lg dark:border-neutral-600"
          >
            <slot name="compactItems" :close="close" />
          </div>
        </Transition>
      </OnClickOutside>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, computed } from 'vue'
import { RouterLink } from 'vue-router'
import { OnClickOutside } from '@vueuse/components'
import UiIcon from '@/components/ui/icon.vue'

const header = ref<HTMLDivElement>()

const { active, icon, name, items, to, allowMultiple = false } = defineProps<{
  active?: boolean
  icon?: string
  name?: string
  items?: any[]
  to?: string
  allowMultiple?: boolean
  minimized?: boolean
  redirect?: string
}>()

const open = ref(false)
const openItems = inject('openItems', ref([]))
const isOpen = computed(() => openItems.value.includes(name))

const toggle = () => {
  if (allowMultiple)
    openItems.value = !isOpen.value
      ? [...openItems.value, name]
      : openItems.value.filter((item) => item !== name)
  else openItems.value = !isOpen.value ? [name] : []

  if (items?.length) open.value = true
}

const close = () => {
  open.value = false
}
</script>

<style scooped lang="scss">
.menu-item {
  a, .trigger-btn {
    @apply relative block rounded-md text-inherit no-underline transition-all hover:text-black dark:text-neutral-300 dark:hover:text-neutral-300;
  }
  div > a, div > .trigger-btn {
    @apply rounded-l-none;
  }

  &:not(.active) > a, &:not(.active) > .trigger-btn {
    @apply hover:bg-neutral-200/75 dark:hover:bg-neutral-700/30 hover:backdrop-blur-md hover:text-[--c-primary-600] dark:hover:text-[--c-primary-200];
  }

  &.active > a, &.active > .trigger-btn {
    @apply bg-[--c-primary-600] font-semibold text-[--c-primary-at-800] hover:text-[--c-primary-at-900] dark:text-[--c-primary-at-600];
  }
}
</style>
